/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Directive,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ViewChild,
  ElementRef,
  HostBinding,
  AfterViewInit,
} from "@angular/core";

import {
  coerceBooleanProperty,
  BooleanInput,
  getDate,
  inputFromDate,
  InputName,
  InputType,
  dateFromInput,
  parse,
  format,
  patternFromFormat,
  getLocalDate,
  startOfDay,
} from "@vattenfall/util";

import { InputCalendarComponent } from "./dates";

import { DatesElementAdapter } from "./dates-adapter";
import { InputsElementAccessor } from "./inputs-accessor";

/**
 * The class name for each calendar icon date listed in order. We are doing this as opposed to a dynamic solution to ensure Tailwind doesn't purge them when building the project.
 */
const CALENDAR_ICONS = <const>[
  "ds-icons-calendar-01",
  "ds-icons-calendar-01",
  "ds-icons-calendar-02",
  "ds-icons-calendar-03",
  "ds-icons-calendar-04",
  "ds-icons-calendar-05",
  "ds-icons-calendar-06",
  "ds-icons-calendar-07",
  "ds-icons-calendar-08",
  "ds-icons-calendar-09",
  "ds-icons-calendar-10",
  "ds-icons-calendar-11",
  "ds-icons-calendar-12",
  "ds-icons-calendar-13",
  "ds-icons-calendar-14",
  "ds-icons-calendar-15",
  "ds-icons-calendar-16",
  "ds-icons-calendar-17",
  "ds-icons-calendar-18",
  "ds-icons-calendar-19",
  "ds-icons-calendar-20",
  "ds-icons-calendar-21",
  "ds-icons-calendar-22",
  "ds-icons-calendar-23",
  "ds-icons-calendar-24",
  "ds-icons-calendar-25",
  "ds-icons-calendar-26",
  "ds-icons-calendar-27",
  "ds-icons-calendar-28",
  "ds-icons-calendar-29",
  "ds-icons-calendar-30",
  "ds-icons-calendar-31",
];

/**
 * The `DatesElementAccessor` contains methods and properties shared by all date-like input components like date, month, week, and year.
 * It require a generic type to be specified, this type should correspond to the value emitted on input change.
 */
@Directive()
export class DatesElementAccessor<T extends InputType>
  extends InputsElementAccessor<T | null, HTMLInputElement>
  implements AfterViewInit
{
  /**
   * Date format string.
   *
   * The characters in the format string wrapped between two single quotes characters (') are escaped. Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
   *
   * Read more at [date-fns](https://date-fns.org/v2.29.3/docs/parse).
   */
  @Input() format = "";
  /**
   * The value of the control.
   *
   * A string representing the date entered in the input. The date is formatted according to ISO8601, described in Date strings format.
   */
  @Input()
  get value(): T | null {
    if (this.ngControl) return this.ngControl.control?.value;
    return this._value;
  }
  set value(value: T | null) {
    this._value = value;
    // Converting ISO input string to a string that matches the format
    const input = this._value ? this.getInputFromDate(dateFromInput(this._value)) : null;
    this.setProperty("value", input);
    if (this.ngControl) this.ngControl.control?.setValue(this._value);
  }
  private _value: T | null = null;
  /**
   * The latest date to accept for `value`.
   *
   * Default is `null`.
   */
  @Input()
  get max(): T | null {
    return this._max;
  }
  set max(value: T | null) {
    this._max = value;
    this.setProperty("max", value);
  }
  private _max: T | null = null;
  /**
   * The earliest date to accept for `value`.
   *
   * Default is `null`.
   */
  @Input()
  get min(): T | null {
    return this._min;
  }
  set min(value: T | null) {
    this._min = value;
    this.setProperty("min", value);
  }
  private _min: T | null = null;
  /**
   * Event emitted when the input value changes with the value represented as a `Date` object.
   */
  @Output() readonly changeAsDate: EventEmitter<Date | null> = new EventEmitter<Date | null>();
  /**
   * The date value of the control.
   *
   * A `Date` object representing the date entered in the input.
   */
  @Input()
  get valueAsDate(): Date | null {
    return this.value ? dateFromInput(this.value) : null;
  }
  set valueAsDate(value: Date | null) {
    this.value = inputFromDate<T>(this.type as InputName, value);
  }
  /**
   * The earliest date to accept for `valueAsDate`.
   *
   * Default is `null`.
   */
  @Input()
  get minAsDate(): Date | null {
    return this.min ? dateFromInput(this.min) : null;
  }
  set minAsDate(value: Date | null) {
    this.min = inputFromDate<T>(this.type as InputName, value);
  }
  /**
   * The latest date to accept for `valueAsDate`.
   *
   * Default is `null`.
   */
  @Input()
  get maxAsDate(): Date | null {
    return this.max ? dateFromInput(this.max) : null;
  }
  set maxAsDate(value: Date | null) {
    this.max = inputFromDate<T>(this.type as InputName, value);
  }
  /**
   * An attribute which, if present, indicates that the user should not be able to edit the value of the input.
   */
  @Input()
  get readonly(): boolean {
    return this._readonly;
  }
  set readonly(value: BooleanInput) {
    this._readonly = coerceBooleanProperty(value);
    this.setProperty("readOnly", this._readonly);
  }
  private _readonly = false;
  /**
   * Whether the form field should stretch the entire width of its parent. Default is `false`.
   */
  @Input()
  get fluid(): boolean {
    return this._fluid;
  }
  set fluid(value: BooleanInput) {
    this._fluid = coerceBooleanProperty(value);
  }
  private _fluid = false;

  @ViewChild(InputCalendarComponent, { read: ElementRef }) calendarElement!: ElementRef<HTMLElement>;

  /** Close calendar when `click` event is fired outside of the input or calendar */
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: PointerEvent) {
    if (this.open) {
      // Remove last item of event path since it's a 'Window' object (and not a 'Node').
      const composedPath = event.composedPath().slice(0, -1) as Array<Node>;
      // Ignore clicks inside input element or calendar
      const isInside = composedPath.some(
        (node) =>
          this.calendarElement.nativeElement.isSameNode(node) || this.inputElement.nativeElement.parentNode!.isSameNode(node)
      );
      if (!isInside) this.onOpenChange();
    }
  }

  /** Make sure host element is positioned relative to ensure calender width is calculated correctly */
  @HostBinding("style.position") readonly position = "relative";

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this._datesElementAdapter = new DatesElementAdapter(this.inputElement.nativeElement, this.format);
    this.inputElement.nativeElement.addEventListener("mousedown", () => {
      this._isMouseEvent = true;
    });
    this.inputElement.nativeElement.addEventListener("focus", () => {
      this.setProperty("value", this._datesElementAdapter.value!);
      // Always set focus to the first selection range when entering using keyboard
      if (!this._isMouseEvent) this._datesElementAdapter.setSelection(0);
      this.onTouched();
    });
    this.inputElement.nativeElement.addEventListener("mouseup", () => {
      this._isMouseEvent = false;
      this._datesElementAdapter.setSelection(this.inputElement.nativeElement.selectionStart ?? 0);
    });
    this.inputElement.nativeElement.addEventListener("blur", () => {
      if (!this.value) {
        // Clear input mask if user has not entered any information
        this.setProperty("value", null);
      }
    });
    this.inputElement.nativeElement.addEventListener("input", () => {
      // We are only dispatching change event when the inputted value is valid
      // otherwise we will mask the input string to give some indication to the user
      // how to complete the input
      if (new RegExp(this.pattern).test(this._datesElementAdapter.value!)) {
        const date = this.getDateFromInput(this._datesElementAdapter.value!);
        this.onDateChange(date);
      } else {
        this.setProperty("value", this._datesElementAdapter.value);
      }
      this._datesElementAdapter.setSelection();
    });
    this.inputElement.nativeElement.addEventListener("keydown", (event: KeyboardEvent) => {
      switch (event.key) {
        case "Tab": {
          this._datesElementAdapter.setSelectionNext(event.preventDefault.bind(event));
          break;
        }
        case "ArrowRight": {
          this._datesElementAdapter.setSelectionNext();
          event.preventDefault();
          break;
        }
        case "ArrowLeft": {
          this._datesElementAdapter.setSelectionPrevious();
          event.preventDefault();
          break;
        }
        case "Backspace": {
          this._datesElementAdapter.clearInputMask();
          break;
        }
        case " ": {
          this.open = !this.open;
          break;
        }
        default: {
          // Only process numbers from here on
          if (!/[0-9]/.test(event.key)) {
            event.preventDefault();
            return;
          }
          this._datesElementAdapter.setInputMask(event.key);
        }
      }
    });
  }
  onOpenChange() {
    this.open = !this.open;
  }
  /** Invoked when a date has been selected in the calendar by the user. */
  onDateChange(date: Date) {
    this.valueAsDate = date;
    this.activeDate = date;
    this.changeAsDate.emit(date);
    const dateString = inputFromDate<T>(this.type as InputName, date);
    // When a date is selected from the calendar then the input is no longer pristine
    // this ensures that the input validates correctly when dates are selected
    // for the first time
    this.onTouched();
    this.onChange(dateString);
    // Close the calendar upon selection
    this.onOpenChange();
  }
  /** Keeps track of the activate date selected in calendar */
  activeDate = startOfDay(new Date());
  /* Keep tracks of the select open and close state */
  open = false;
  /** Name of icon class that displays the selected date. */
  get currentDateIcon() {
    return CALENDAR_ICONS[getDate(this.activeDate) - 1];
  }
  /** `RegExp` pattern of the provided date format. */
  get pattern() {
    return patternFromFormat(this.format);
  }
  /** Keeps track of whether the input element was focused as a result of a mouse or keyboard event. */
  private _isMouseEvent = false;
  /** Instantiated elements adapter. */
  private _datesElementAdapter!: DatesElementAdapter;
  /** Returns `Date` object from string value of input field. */
  private getDateFromInput(input: string) {
    return getLocalDate(
      parse(input, this.format, new Date(), {
        weekStartsOn: 4,
        useAdditionalWeekYearTokens: true,
      })
    );
  }
  /** Returns string input value from `Date` object. */
  private getInputFromDate(date: Date) {
    return format(date, this.format, {
      weekStartsOn: 4,
      useAdditionalWeekYearTokens: true,
    }) as T;
  }
}
